/** @jsx jsx */
/** @jsxFrag React.Fragment */
import React from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';
import { HtmlAnchor } from '@helpcenter/analytics-components/src/html-anchor';
import { token } from '@atlaskit/tokens';
import IconButton from '@atlaskit/button/icon/button';
import CustomizeIcon from '@atlaskit/icon/core/customize';
import ButtonItem from '@atlaskit/menu/button-item';
import CustomItem from '@atlaskit/menu/custom-item';
import HeadingItem from '@atlaskit/menu/heading-item';
import MenuGroup from '@atlaskit/menu/menu-group';
import Section from '@atlaskit/menu/section';
import { Popup } from '@atlaskit/popup/popup';
import type { TriggerProps } from '@atlaskit/popup/types';

import { useSetCustomiseSidebarTrigger } from 'view/common/pushing-customize-sidebar';
import { useMediaQuery } from 'view/help-center/use-screen-size/use-screen-size';

import hubMessages from '../messages';

interface HubCustomizeMenuItemBase {
	label: string;
	testId: string;
	/**
	 * Renders the whole item. For items that need to wrap themselves in something —
	 * the Premium ones wrap a modal trigger — so this component stays unaware of
	 * what they wrap in. Mirrors `customRenderer` in the legacy avatar menu.
	 */
	customRenderer?: () => React.ReactNode;
}

export interface HubCustomizeMenuButtonItem extends HubCustomizeMenuItemBase {
	onClick: () => void;
	href?: never;
}

/**
 * Every link target leaves the help seeker route tree — help center management,
 * or Jira once it owns it — so these render as anchors rather than router links.
 * `actionSubjectId` is required because the anchor is what fires the click event.
 */
export interface HubCustomizeMenuLinkItem extends HubCustomizeMenuItemBase {
	href: string;
	actionSubjectId: string;
	onClick?: () => void;
}

export type HubCustomizeMenuItem = HubCustomizeMenuButtonItem | HubCustomizeMenuLinkItem;

export interface HubCustomizeMenuSection {
	heading?: string;
	items: HubCustomizeMenuItem[];
}

interface HubCustomizeMenuProps {
	sections: HubCustomizeMenuSection[];
	iconColor?: string;
}

const menuContainerStyles = css({
	width: 234,
	backgroundColor: token('elevation.surface', '#FFFFFF'),
	borderRadius: token('radius.small', '3px'),
	boxShadow: token('elevation.shadow.overlay', '0 8px 12px #091e4226'),
});

const setRefValue = (ref: TriggerProps['ref'], element: HTMLButtonElement | null) => {
	if (typeof ref === 'function') {
		ref(element);
	} else if (ref && typeof ref === 'object' && 'current' in ref) {
		(ref as React.MutableRefObject<HTMLElement | null>).current = element;
	}
};

const HubCustomizeMenuComponent = ({ sections, iconColor }: HubCustomizeMenuProps) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const { formatMessage } = useIntl();
	const isMobile = useMediaQuery('(max-width: 768px)');
	const setCustomiseSidebarTrigger = useSetCustomiseSidebarTrigger();

	const handleToggle = React.useCallback(() => {
		setIsOpen((prev) => !prev);
	}, []);

	const handleItemClick = React.useCallback((onClick?: () => void) => {
		onClick?.();
		setIsOpen(false);
	}, []);

	const visibleSections = React.useMemo(
		() => sections.filter((section) => section.items.length > 0),
		[sections],
	);

	const renderContent = React.useCallback(
		() => (
			<div data-testid="hub-customize-menu-content" css={menuContainerStyles}>
				<MenuGroup spacing="cozy">
					{visibleSections.map((section, index) => (
						<Section key={section.heading ?? `section-${index}`} hasSeparator={index > 0}>
							{section.heading && (
								<HeadingItem id={`hub-customize-menu-heading-${index}`}>
									{section.heading}
								</HeadingItem>
							)}
							{section.items.map((item) => {
								if (item.customRenderer) {
									return <React.Fragment key={item.testId}>{item.customRenderer()}</React.Fragment>;
								}
								return item.href ? (
									<CustomItem
										key={item.testId}
										component={HtmlAnchor}
										href={item.href}
										actionSubjectId={item.actionSubjectId}
										testId={item.testId}
										onClick={() => handleItemClick(item.onClick)}
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling
										className="cv-exclude-from-customisation"
									>
										{item.label}
									</CustomItem>
								) : (
									<ButtonItem
										key={item.testId}
										testId={item.testId}
										onClick={() => handleItemClick(item.onClick)}
									>
										{item.label}
									</ButtonItem>
								);
							})}
						</Section>
					))}
				</MenuGroup>
			</div>
		),
		[visibleSections, handleItemClick],
	);

	const renderTrigger = React.useCallback(
		({ ref, ...triggerProps }: TriggerProps) => {
			const combinedRef = (element: HTMLButtonElement | null) => {
				setRefValue(ref, element);
				setCustomiseSidebarTrigger?.(element);
			};

			return (
				<IconButton
					{...triggerProps}
					ref={combinedRef}
					appearance="subtle"
					icon={(iconProps) =>
						iconColor ? (
							<span data-testid="hub-customize-menu-icon" style={{ color: iconColor }}>
								<CustomizeIcon {...iconProps} />
							</span>
						) : (
							<CustomizeIcon {...iconProps} />
						)
					}
					label={formatMessage(hubMessages.customizeMenuLabel)}
					testId="hub-customize-menu-trigger"
					onClick={handleToggle}
				/>
			);
		},
		[formatMessage, handleToggle, iconColor, setCustomiseSidebarTrigger],
	);

	if (visibleSections.length === 0 || isMobile) {
		return null;
	}

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement="bottom-end"
			shouldRenderToParent
			content={renderContent}
			trigger={renderTrigger}
		/>
	);
};

HubCustomizeMenuComponent.displayName = 'HubCustomizeMenu';

export const HubCustomizeMenu = React.memo(HubCustomizeMenuComponent);
